/**
 * Chat Store - State management for integrated chat system
 *
 * Manages multiple chat sessions, window states, and message history
 * Similar to VS Code Copilot chat integration
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  releaseCaseChatWebSocket,
  releaseChatWebSocketsForCases,
} from "./chatTransportStore";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  suggestions?: string[];
}

export interface ChatSession {
  id: string;
  caseId: string;
  caseTitle: string;
  messages: ChatMessage[];
  isActive: boolean;
  createdAt: Date;
  lastMessageAt: Date;
}

export interface ChatWindowState {
  isOpen: boolean;
  isMinimized: boolean;
  width: number;
  height: number;
  position: "right" | "left";
}

interface ChatStoreState {
  // Chat sessions
  sessions: ChatSession[];
  activeSessionId: string | null;

  // Window state
  windowState: ChatWindowState;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  openChat: (caseId: string, caseTitle: string) => void;
  closeChat: (sessionId: string) => void;
  setActiveSession: (sessionId: string) => void;
  addMessage: (
    sessionId: string,
    message: Omit<ChatMessage, "id" | "timestamp">,
  ) => void;
  updateMessage: (
    sessionId: string,
    messageId: string,
    updates: Partial<ChatMessage>,
  ) => void;
  clearSession: (sessionId: string) => void;

  // Window actions
  toggleChat: () => void;
  minimizeChat: () => void;
  maximizeChat: () => void;
  closeWindow: () => void;
  resizeWindow: (width: number, height: number) => void;

  /**
   * Register a case chat session without forcing the window open.
   * Called by CaseDetailPage so the sidebar stays in sync with the
   * caseChatMessagesStore without hijacking window state.
   */
  registerSession: (caseId: string, caseTitle: string) => void;

  /**
   * Bump lastMessageAt for a session so the sidebar timestamp refreshes
   * when messages are written to caseChatMessagesStore from ChatWindow.
   */
  touchSession: (sessionId: string) => void;

  // Utility
  getActiveSession: () => ChatSession | null;
  getSessionByCaseId: (caseId: string) => ChatSession | null;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearAllSessions: () => void;
}

const DEFAULT_WINDOW_STATE: ChatWindowState = {
  isOpen: false,
  isMinimized: false,
  width: 700,
  height: 600,
  position: "right",
};

export const useChatStore = create<ChatStoreState>()(
  persist(
    (set, get) => ({
      sessions: [],
      activeSessionId: null,
      windowState: DEFAULT_WINDOW_STATE,
      isLoading: false,
      error: null,

      openChat: (caseId: string, caseTitle: string) => {
        const existingSession = get().sessions.find((s) => s.caseId === caseId);

        if (existingSession) {
          // Reactivate existing session
          set({
            activeSessionId: existingSession.id,
            windowState: {
              ...get().windowState,
              isOpen: true,
              isMinimized: false,
            },
          });
        } else {
          // Create new session
          const newSession: ChatSession = {
            id: `chat-${Date.now()}-${crypto.randomUUID()}`,
            caseId,
            caseTitle,
            messages: [
              {
                id: `msg-${Date.now()}`,
                role: "system",
                content: `Chat session started for case: ${caseTitle}`,
                timestamp: new Date(),
              },
            ],
            isActive: true,
            createdAt: new Date(),
            lastMessageAt: new Date(),
          };

          set({
            sessions: [...get().sessions, newSession],
            activeSessionId: newSession.id,
            windowState: {
              ...get().windowState,
              isOpen: true,
              isMinimized: false,
            },
          });
        }
      },

      closeChat: (sessionId: string) => {
        const victim = get().sessions.find((s) => s.id === sessionId);
        if (victim) {
          const cid = parseInt(victim.caseId, 10);
          if (!Number.isNaN(cid) && cid > 0) {
            releaseCaseChatWebSocket(cid);
          }
        }
        const sessions = get().sessions.filter((s) => s.id !== sessionId);
        const activeSessionId =
          get().activeSessionId === sessionId
            ? sessions.length > 0
              ? sessions[sessions.length - 1].id
              : null
            : get().activeSessionId;

        set({
          sessions,
          activeSessionId,
          windowState:
            sessions.length === 0
              ? { ...get().windowState, isOpen: false }
              : get().windowState,
        });
      },

      setActiveSession: (sessionId: string) => {
        if (get().sessions.find((s) => s.id === sessionId)) {
          set({ activeSessionId: sessionId });
        }
      },

      addMessage: (
        sessionId: string,
        message: Omit<ChatMessage, "id" | "timestamp">,
      ) => {
        const sessions = get().sessions.map((session) => {
          if (session.id === sessionId) {
            const newMessage: ChatMessage = {
              ...message,
              id: `msg-${Date.now()}-${crypto.randomUUID()}`,
              timestamp: new Date(),
            };

            return {
              ...session,
              messages: [...session.messages, newMessage],
              lastMessageAt: new Date(),
            };
          }
          return session;
        });

        set({ sessions });
      },

      updateMessage: (
        sessionId: string,
        messageId: string,
        updates: Partial<ChatMessage>,
      ) => {
        const sessions = get().sessions.map((session) => {
          if (session.id === sessionId) {
            return {
              ...session,
              messages: session.messages.map((msg) =>
                msg.id === messageId ? { ...msg, ...updates } : msg,
              ),
              lastMessageAt: new Date(),
            };
          }
          return session;
        });

        set({ sessions });
      },

      clearSession: (sessionId: string) => {
        const sessions = get().sessions.map((session) => {
          if (session.id === sessionId) {
            return {
              ...session,
              messages: [
                {
                  id: `msg-${Date.now()}`,
                  role: "system" as const,
                  content: "Chat history cleared",
                  timestamp: new Date(),
                },
              ],
            };
          }
          return session;
        });

        set({ sessions });
      },

      toggleChat: () => {
        set({
          windowState: {
            ...get().windowState,
            isOpen: !get().windowState.isOpen,
            isMinimized: false,
          },
        });
      },

      minimizeChat: () => {
        set({
          windowState: {
            ...get().windowState,
            isMinimized: true,
          },
        });
      },

      maximizeChat: () => {
        set({
          windowState: {
            ...get().windowState,
            isMinimized: false,
          },
        });
      },

      closeWindow: () => {
        const caseIds = get()
          .sessions.map((s) => parseInt(s.caseId, 10))
          .filter((cid) => !Number.isNaN(cid) && cid > 0);
        releaseChatWebSocketsForCases(caseIds);
        set({
          windowState: {
            ...get().windowState,
            isOpen: false,
          },
        });
      },

      resizeWindow: (width: number, height: number) => {
        set({
          windowState: {
            ...get().windowState,
            width: Math.max(300, Math.min(1400, width)),
            height: Math.max(400, Math.min(1000, height)),
          },
        });
      },

      getActiveSession: () => {
        const { sessions, activeSessionId } = get();
        return sessions.find((s) => s.id === activeSessionId) || null;
      },

      getSessionByCaseId: (caseId: string) => {
        return get().sessions.find((s) => s.caseId === caseId) || null;
      },

      setLoading: (isLoading: boolean) => set({ isLoading }),

      setError: (error: string | null) => set({ error }),

      registerSession: (caseId, caseTitle) => {
        const existing = get().sessions.find((s) => s.caseId === caseId);
        if (existing) {
          // Keep it active + refresh the title in case it changed
          set({
            activeSessionId: existing.id,
            sessions: get().sessions.map((s) =>
              s.id === existing.id ? { ...s, caseTitle } : s,
            ),
          });
        } else {
          const newSession: ChatSession = {
            id: `chat-${Date.now()}-${crypto.randomUUID()}`,
            caseId,
            caseTitle,
            messages: [],
            isActive: true,
            createdAt: new Date(),
            lastMessageAt: new Date(),
          };
          set({
            sessions: [...get().sessions, newSession],
            activeSessionId: newSession.id,
          });
        }
      },

      touchSession: (sessionId) => {
        set({
          sessions: get().sessions.map((s) =>
            s.id === sessionId ? { ...s, lastMessageAt: new Date() } : s,
          ),
        });
      },

      clearAllSessions: () => {
        const caseIds = get()
          .sessions.map((s) => parseInt(s.caseId, 10))
          .filter((cid) => !Number.isNaN(cid) && cid > 0);
        releaseChatWebSocketsForCases(caseIds);
        set({
          sessions: [],
          activeSessionId: null,
          windowState: DEFAULT_WINDOW_STATE,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: "alpha-health-chat-store",
      partialize: (state) => ({
        sessions: state.sessions,
        activeSessionId: state.activeSessionId,
        windowState: state.windowState,
      }),
    },
  ),
);
